import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';
import { Rule, RuleToPi } from '../../../../../models/rule.model';
import { ChartData, ChartOptions } from 'chart.js';
import { PiPoint, PiPoints } from '../../../../../models/pi-data.model';
import {
  ValidatedPiPointInfoDto,
  EvaluationConfigDto,
  ValidationDto,
} from '../../../../../models/rule-creation-chat.model';
import { PiDataService } from '../../../../../services/pi-data.service';
import { RuleService } from '../../../../../services/rule.service';
import { TIME_FRAME } from '../../../../constants/filters.constants';
import { computeThresholdDate } from '../../../../../../utils/date-utils';
import { DefaultChartOptions } from '../../../../../../utils/chart/default-options.utils';
import { PiDataPointsDatasetConverter } from '../../../../../../utils/chart/pi-datapoints-dataset-converter.utils';
import { RuleEvaluationDatasetConverter } from '../../../../../../utils/chart/rule-evaluation-dataset-converter.utils';
import {
  selectIsLoadingPiPointsByRule,
  selectSelectedPiPoints,
} from '../../../../../store/selectors/pi-data.selector';
import * as piDataActions from '../../../../../store/actions/pi-data.action';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-equations-step',
  templateUrl: './equations-step.component.html',
  standalone: false,
})
export class EquationsStepComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() rule?: Rule;
  @Input() mode: 'create' | 'edit' = 'edit';

  private destroy$ = new Subject<void>();

  isLoadingPiPointsByRule$ = new Observable<boolean>();

  interactivePiChartDatasets: ChartData<'line'> | undefined;
  piPointsChartOptions: ChartOptions<'line'> = {};
  interactiveChartDatasets: ChartData<'line'> | undefined;
  ruleEvaluationChartOptions: ChartOptions<'line'> = {};
  selectedPiPoints: PiPoint[] = [];
  private previousPiPoints: PiPoint[] = [];
  private interpolationResolutionInMinutes = 60;
  private cachedPiPointsData: PiPoints | null = null;
  private cachedPiPointDetails: any = null;
  private cachedStartDate: Date | null = null;
  private cachedEndDate: Date | null = null;

  evaluationLoading = false;
  evaluationError = false;
  evaluationErrorDetail = '';
  loadingPiData = false;
  largePeriodWarning = false;

  evaluationModeOptions: SelectItem[] = [
    { label: '1/1', value: '1/1' },
    { label: '2/3', value: '2/3' },
    { label: '4/5', value: '4/5' },
    { label: 'Manuel', value: 'manuel' },
  ];

  get showManualEvaluationFields(): boolean {
    return this.formGroup.get('evaluationMode')?.value === 'manuel';
  }

  get isAgentModeAvailable(): boolean {
    if (this.mode === 'edit') {
      return !!this.rule?.uid;
    }
    return !!this.formGroup.get('functionalLocation')?.value;
  }

  get selectedEquipmentId(): string {
    return this.formGroup.get('functionalLocation')?.value ?? '';
  }

  get selectedRuleId(): string {
    return this.rule?.uid ?? '';
  }

  agentMode = false;

  private lastAgentPiPointTags: string[] = [];
  private lastAgentInterpolationInterval: string | null = null;
  private lastAgentEvaluationPeriod: string | null = null;
  private lastAgentMonitoringEquation: string | null = null;
  private lastAgentActivationCondition: string | null = null;
  private lastAgentValidationSuccess = false;
  private lastEvaluatedSnapshot: string | null = null;
  private static readonly DEFAULT_AGENT_INTERPOLATION_MINUTES = 10;

  activationConditionSyntaxValid = true;
  activationConditionPipointValid = true;
  pythonCodeSyntaxValid = true;
  pythonCodePipointValid = true;
  activationConditionPiValidating = false;
  pythonCodePiValidating = false;

  constructor(
    private store: Store,
    private piDataService: PiDataService,
    private ruleService: RuleService,
  ) {}

  ngOnInit(): void {
    if (this.mode === 'edit' && this.rule?.uid) {
      this.store.dispatch(
        piDataActions.loadPiPointsByRuleId({ ruleUid: this.rule.uid }),
      );
    }

    this.isLoadingPiPointsByRule$ = this.store.select(
      selectIsLoadingPiPointsByRule,
    );

    this.piPointsChartOptions = DefaultChartOptions.getOptions(
      TIME_FRAME.THREEMONTH,
      'MMM dd, HH:mm',
      'Valeur',
      false,
    );

    this.ruleEvaluationChartOptions = DefaultChartOptions.getOptions(
      'day',
      'MMM dd, HH:mm',
      'Valeur',
    );

    this.store
      .select(selectSelectedPiPoints)
      .pipe(takeUntil(this.destroy$))
      .subscribe((piPoints: PiPoint[]) => {
        this.selectedPiPoints = piPoints;
        this.loadPiInfo(piPoints);
      });

    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(piDataActions.clearSelectedPiPoints());
  }

  get hasErrors(): boolean {
    const fields = [
      'pythonCode',
      'activationConditionPythonExpression',
      'evaluationStartDate',
      'evaluationEndDate',
      'alertThreshold',
      'toleranceThreshold',
      'minAlertThreshold',
      'minToleranceThreshold',
      'periodInMinutes',
      'piPointInterpolationIntervalInMinutes',
      'evaluationMode',
      'minEvaluationsToTriggerAlarm',
      'evaluationsToConsiderForAlarm',
    ];
    const formErrors = fields.some((f) => {
      const ctrl = this.formGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
    return formErrors || !this.isPythonCodeValid();
  }

  isPythonCodeValid(): boolean {
    return (
      this.activationConditionSyntaxValid &&
      this.activationConditionPipointValid &&
      this.pythonCodeSyntaxValid &&
      this.pythonCodePipointValid
    );
  }

  canEvaluateRule(): boolean {
    const periodInMinutes = this.formGroup.get('periodInMinutes')?.value;
    return (
      this.isPythonCodeValid() &&
      this.formGroup.get('evaluationStartDate')?.valid &&
      this.formGroup.get('evaluationEndDate')?.valid &&
      periodInMinutes &&
      !this.loadingPiData &&
      !this.activationConditionPiValidating &&
      !this.pythonCodePiValidating &&
      this.selectedPiPoints.length > 0 &&
      !!this.interactivePiChartDatasets
    );
  }

  getEvaluationTooltip(): string {
    if (!this.isPythonCodeValid()) {
      return 'Veuillez corriger les erreurs de syntaxe Python ou de validation des points PI.';
    }
    if (this.loadingPiData) return 'Chargement des données PI en cours...';
    if (this.activationConditionPiValidating || this.pythonCodePiValidating) {
      return 'Validation des points PI en cours...';
    }
    if (this.selectedPiPoints.length === 0) {
      return 'Veuillez sélectionner au moins un point PI.';
    }
    if (!this.interactivePiChartDatasets) {
      return 'Les données PI ne sont pas encore chargées.';
    }
    if (!this.formGroup.get('periodInMinutes')?.value) {
      return "Veuillez définir la période d'évaluation.";
    }
    if (!this.formGroup.get('evaluationStartDate')?.valid) {
      return 'Veuillez sélectionner une date de début valide.';
    }
    if (!this.formGroup.get('evaluationEndDate')?.valid) {
      return 'Veuillez sélectionner une date de fin valide.';
    }
    return '';
  }

  onActivationConditionSyntaxValidation(event: {
    isValid: boolean;
    error?: string;
  }): void {
    this.activationConditionSyntaxValid = event.isValid;
  }

  onActivationConditionPiPointValidation(event: {
    isValid: boolean;
    errors: string[];
  }): void {
    this.activationConditionPipointValid = event.isValid;
  }

  onActivationConditionPiPointValidationLoading(loading: boolean): void {
    this.activationConditionPiValidating = loading;
  }

  onPythonCodeSyntaxValidation(event: {
    isValid: boolean;
    error?: string;
  }): void {
    this.pythonCodeSyntaxValid = event.isValid;
  }

  onPythonCodePiPointValidation(event: {
    isValid: boolean;
    errors: string[];
  }): void {
    this.pythonCodePipointValid = event.isValid;
  }

  onPythonCodePiPointValidationLoading(loading: boolean): void {
    this.pythonCodePiValidating = loading;
  }

  onAgentEvaluationConfigChange(config: EvaluationConfigDto): void {
    if (
      config.interpolationInterval &&
      config.interpolationInterval !== this.lastAgentInterpolationInterval
    ) {
      this.lastAgentInterpolationInterval = config.interpolationInterval;
      const minutes = this.parseInterpolationIntervalToMinutes(
        config.interpolationInterval,
      );
      if (minutes != null) {
        const control = this.formGroup.get(
          'piPointInterpolationIntervalInMinutes',
        );
        if (control && control.value !== minutes) {
          control.setValue(minutes);
        }
      }
    }

    if (config.evaluationPeriod) {
      const periodMinutes = this.parseInterpolationIntervalToMinutes(
        config.evaluationPeriod,
      );
      if (periodMinutes != null) {
        this.lastAgentEvaluationPeriod = config.evaluationPeriod;
        const control = this.formGroup.get('periodInMinutes');
        if (control && control.value !== periodMinutes) {
          control.setValue(periodMinutes);
        }
      }
    }

    this.tryAgentEvaluate();
  }

  onAgentValidationComplete(validation: ValidationDto): void {
    this.lastAgentValidationSuccess = validation.success;
    this.tryAgentEvaluate();
  }

  onAgentValidatedPiPointsChange(piPoints: ValidatedPiPointInfoDto[]): void {
    const newTags = piPoints.map((p) => p.tag).sort();
    const oldTags = [...this.lastAgentPiPointTags].sort();
    if (
      newTags.length === oldTags.length &&
      newTags.every((t, i) => t === oldTags[i])
    ) {
      return;
    }
    this.lastAgentPiPointTags = newTags;

    // Set default interpolation if not already set by agent
    const interpolationControl = this.formGroup.get(
      'piPointInterpolationIntervalInMinutes',
    );
    if (
      interpolationControl &&
      !this.lastAgentInterpolationInterval &&
      (!interpolationControl.value ||
        interpolationControl.value === this.interpolationResolutionInMinutes)
    ) {
      interpolationControl.setValue(
        EquationsStepComponent.DEFAULT_AGENT_INTERPOLATION_MINUTES,
        { emitEvent: false },
      );
    }

    const storePiPoints: PiPoint[] = piPoints.map((p) => ({
      tag: p.tag,
      webId: p.webId,
      server: p.server,
      ruleToPis: [],
    }));
    this.store.dispatch(
      piDataActions.setSelectedPiPoints({ piPoints: storePiPoints }),
    );
  }

  private parseInterpolationIntervalToMinutes(interval: string): number | null {
    const match = interval.match(/^(\d+)\s*(m|min|minutes?|h|hours?)$/i);
    if (!match) return null;
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    if (unit.startsWith('h')) return value * 60;
    return value;
  }

  onAgentMonitoringEquationChange(equation: string): void {
    if (equation === this.lastAgentMonitoringEquation) return;
    this.lastAgentMonitoringEquation = equation;
    this.lastAgentValidationSuccess = false;

    const control = this.formGroup.get('pythonCode');
    if (control) {
      control.setValue(equation);
    }
  }

  onAgentActivationConditionChange(condition: string): void {
    if (condition === this.lastAgentActivationCondition) return;
    this.lastAgentActivationCondition = condition;
    this.lastAgentValidationSuccess = false;

    const control = this.formGroup.get('activationConditionPythonExpression');
    if (control) {
      control.setValue(condition);
    }
  }

  private tryAgentEvaluate(): void {
    if (
      !this.lastAgentValidationSuccess ||
      !this.lastAgentMonitoringEquation ||
      !this.lastAgentActivationCondition ||
      !this.lastAgentInterpolationInterval ||
      !this.lastAgentEvaluationPeriod ||
      this.selectedPiPoints.length === 0 ||
      !this.interactivePiChartDatasets ||
      this.loadingPiData
    ) {
      return;
    }

    const snapshot = JSON.stringify({
      eq: this.lastAgentMonitoringEquation,
      ac: this.lastAgentActivationCondition,
      int: this.lastAgentInterpolationInterval,
      per: this.lastAgentEvaluationPeriod,
      tags: this.lastAgentPiPointTags,
    });

    if (snapshot === this.lastEvaluatedSnapshot) return;
    this.lastEvaluatedSnapshot = snapshot;
    this.lastAgentValidationSuccess = false;
    this.onEvaluate();
  }

  private setupFormSubscriptions(): void {
    const evaluationDateChanges$ = combineLatest([
      this.formGroup
        .get('evaluationStartDate')!
        .valueChanges.pipe(startWith(null)),
      this.formGroup
        .get('evaluationEndDate')!
        .valueChanges.pipe(startWith(null)),
    ]).pipe(takeUntil(this.destroy$));

    evaluationDateChanges$.subscribe(() => {
      this.formGroup
        .get('evaluationStartDate')
        ?.updateValueAndValidity({ emitEvent: false });
      this.formGroup
        .get('evaluationEndDate')
        ?.updateValueAndValidity({ emitEvent: false });
      this.checkAndReloadPiDataIfNeeded();
    });

    // Evaluation mode changes
    this.formGroup
      .get('evaluationMode')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((mode: string) => {
        this.onEvaluationModeChange(mode);
      });

    // Evaluation parameter cross-validation
    const evaluationFields = [
      'minEvaluationsToTriggerAlarm',
      'evaluationsToConsiderForAlarm',
    ];
    evaluationFields.forEach((fieldName) => {
      this.formGroup
        .get(fieldName)!
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          evaluationFields.forEach((otherField) => {
            if (otherField !== fieldName) {
              this.formGroup
                .get(otherField)
                ?.updateValueAndValidity({ emitEvent: false });
            }
          });
        });
    });

    this.formGroup
      .get('piPointInterpolationIntervalInMinutes')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cachedPiPointsData = null;
        this.cachedPiPointDetails = null;
        this.previousPiPoints = [];
        if (this.selectedPiPoints.length > 0) {
          this.loadPiInfo(this.selectedPiPoints);
        }
      });
  }

  toRuleToPi(p: PiPoint): RuleToPi {
    return {
      piPointFK: p.webId,
      ruleFK: '',
      piPoint: p,
      rule: undefined,
    };
  }

  private getEffectiveStartDate(): Date {
    const formStart = this.formGroup.get('evaluationStartDate')?.value;
    return formStart
      ? new Date(formStart)
      : computeThresholdDate(TIME_FRAME.THREEMONTH);
  }

  private getEffectiveEndDate(): Date {
    const formEnd = this.formGroup.get('evaluationEndDate')?.value;
    return formEnd ? new Date(formEnd) : new Date();
  }

  private checkAndReloadPiDataIfNeeded(): void {
    const startDate = this.getEffectiveStartDate();
    const endDate = this.getEffectiveEndDate();

    const needsReload =
      this.cachedStartDate &&
      this.cachedEndDate &&
      (startDate.getTime() < this.cachedStartDate.getTime() ||
        endDate.getTime() > this.cachedEndDate.getTime());

    if (
      needsReload &&
      this.selectedPiPoints.length > 0 &&
      !this.loadingPiData
    ) {
      // Invalidate cache and reload for the new range
      this.cachedPiPointsData = null;
      this.cachedPiPointDetails = null;
      this.previousPiPoints = [];
      this.loadPiInfo(this.selectedPiPoints);
    } else {
      this.updateChartWithFilteredData();
    }
  }

  loadPiInfo(piPoints: PiPoint[]): void {
    if (!piPoints || piPoints.length === 0) {
      this.interactivePiChartDatasets = undefined;
      this.previousPiPoints = [];
      this.cachedPiPointsData = null;
      this.cachedPiPointDetails = null;
      this.loadingPiData = false;
      return;
    }

    const interpolationResolutionInMinutes =
      this.formGroup.get('piPointInterpolationIntervalInMinutes')?.value ??
      this.interpolationResolutionInMinutes;

    const previousTags = this.previousPiPoints.map((p) => p.tag);
    const currentTags = piPoints.map((p) => p.tag);
    const removedTags = previousTags.filter(
      (tag) => !currentTags.includes(tag),
    );
    const newPoints = piPoints.filter((p) => !previousTags.includes(p.tag));

    const startDate = this.getEffectiveStartDate();
    const endDate = this.getEffectiveEndDate();

    if (
      this.cachedPiPointsData &&
      (removedTags.length > 0 || newPoints.length > 0)
    ) {
      if (removedTags.length > 0) {
        this.cachedPiPointsData = {
          points: this.cachedPiPointsData.points.filter(
            (point) => !removedTags.includes(point.tag),
          ),
        };
      }

      if (newPoints.length > 0) {
        this.loadingPiData = true;
        const newPiPointValues$ = this.piDataService.GetPisValues(
          newPoints.map((p) => this.toRuleToPi(p)),
          startDate,
          endDate,
          interpolationResolutionInMinutes,
        );
        const newPiPointDetails$ = this.piDataService.GetPiPointsDetails(
          newPoints.map((p) => this.toRuleToPi(p)),
        );

        combineLatest([newPiPointValues$, newPiPointDetails$])
          .pipe(takeUntil(this.destroy$))
          .subscribe(([newPiPointsData, newPiPointDetails]) => {
            if (this.cachedPiPointsData) {
              this.cachedPiPointsData.points.push(...newPiPointsData.points);
            } else {
              this.cachedPiPointsData = newPiPointsData;
            }
            this.cachedStartDate = startDate;
            this.cachedEndDate = endDate;

            if (this.cachedPiPointDetails) {
              this.cachedPiPointDetails = {
                ...this.cachedPiPointDetails,
                ...newPiPointDetails,
              };
            } else {
              this.cachedPiPointDetails = newPiPointDetails;
            }

            this.updateChartWithFilteredData();
            this.loadingPiData = false;
            this.tryAgentEvaluate();
          });
      } else {
        this.updateChartWithFilteredData();
      }
    } else {
      this.loadingPiData = true;
      const piPointValues$ = this.piDataService.GetPisValues(
        piPoints.map((p) => this.toRuleToPi(p)),
        startDate,
        endDate,
        interpolationResolutionInMinutes,
      );
      const piPointDetails$ = this.piDataService.GetPiPointsDetails(
        piPoints.map((p) => this.toRuleToPi(p)),
      );

      combineLatest([piPointValues$, piPointDetails$])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([piPointsData, piPointDetails]) => {
          this.cachedPiPointsData = piPointsData;
          this.cachedPiPointDetails = piPointDetails;
          this.cachedStartDate = startDate;
          this.cachedEndDate = endDate;
          this.updateChartWithFilteredData();
          this.loadingPiData = false;
          this.tryAgentEvaluate();
        });
    }

    this.previousPiPoints = [...piPoints];
  }

  onEvaluate(): void {
    if (
      !this.interactivePiChartDatasets ||
      !this.interactivePiChartDatasets.datasets
    ) {
      return;
    }

    this.evaluationLoading = true;
    this.evaluationError = false;

    const startDate = this.formGroup.get('evaluationStartDate')?.value;
    const endDate = this.formGroup.get('evaluationEndDate')?.value;
    const pythonCode = this.formGroup.get('pythonCode')?.value;
    const activationCondition = this.formGroup.get(
      'activationConditionPythonExpression',
    )?.value;

    // Build a per-dataset lookup first, then keep only timestamps present in
    // every dataset so that all value arrays have the same length.
    const datasetMaps: Map<number, any>[] =
      this.interactivePiChartDatasets.datasets.map(
        (d: any) =>
          new Map<number, any>(
            (d.data as any[]).map((pt: any) => [pt.x as number, pt.y]),
          ),
      );

    const commonTimestamps = Array.from(
      new Set(
        this.interactivePiChartDatasets.datasets.flatMap((d: any) =>
          (d.data as any[]).map((pt: any) => pt.x as number),
        ),
      ),
    )
      .filter((t) =>
        datasetMaps.every((m) => {
          const value = m.get(t);
          return m.has(t) && value !== null && value !== undefined;
        }),
      )
      .sort((a, b) => a - b);
    // Check if we have any common timestamps with valid data
    if (commonTimestamps.length === 0) {
      this.evaluationError = true;
      this.evaluationErrorDetail =
        "Aucune donnée commune trouvée pour tous les points PI dans la période sélectionnée. Veuillez ajuster la période d'évaluation ou vérifier que tous les points PI ont des données.";
      this.evaluationLoading = false;
      return;
    }
    const rule_pi_point_values = {
      ts: commonTimestamps.map((t) => this.formatDate(new Date(t))),
      pi_point_values: this.interactivePiChartDatasets.datasets.map(
        (dataset: any, i: number) => {
          if (dataset?.valueLabels) {
            return {
              tag: this.extractTagFromLabel(dataset.label || ''),
              is_num: false,
              num_values: [],
              str_values: commonTimestamps.map((t) => {
                const y = datasetMaps[i].get(t);
                return dataset.valueLabels[y] ?? y.toString();
              }),
            };
          } else {
            return {
              tag: this.extractTagFromLabel(dataset.label || ''),
              is_num: true,
              num_values: commonTimestamps.map((t) => datasetMaps[i].get(t)),
              str_values: [],
            };
          }
        },
      ),
    };

    const piPoints = this.selectedPiPoints.map((piPoint) => ({
      tag: piPoint.tag,
      server: piPoint.server || '',
    }));

    const periodInMinutes = this.formGroup.get('periodInMinutes')?.value;

    const body_data = {
      UniqueId: uuidv4(),
      Version: 1,
      LambdaCode: pythonCode || '',
      ActivationCondition: activationCondition || '',
      Threshold: 0,
      IsAlertActive: false,
      PiPoints: piPoints,
      PeriodInMinutes: periodInMinutes,
      LookbackPeriodInMinutes: periodInMinutes,
      from_date_time: startDate ? this.formatDate(startDate) : '',
      to_date_time: endDate ? this.formatDate(endDate) : '',
      RulePiPointValues: rule_pi_point_values,
    };

    this.ruleService.RuleTesterChunked(body_data).subscribe({
      next: (response) => {
        if (response.evaluations?.length === 0 && response.error_detail) {
          this.evaluationError = true;
          this.evaluationErrorDetail = response.error_detail;
          this.evaluationLoading = false;
          return;
        }

        const evaluations = response.evaluations.map((item: any) => ({
          value: item.value,
          ts: item.iso_date_time,
        }));
        this.interactiveChartDatasets =
          RuleEvaluationDatasetConverter.GenerateChartData(
            TIME_FRAME.SIXMONTH,
            evaluations,
            this.formGroup?.get('periodInMinutes')?.value ?? 60,
            this.formGroup?.get('alertThreshold')?.value,
            this.formGroup?.get('toleranceThreshold')?.value,
            this.formGroup?.get('minAlertThreshold')?.value,
            this.formGroup?.get('minToleranceThreshold')?.value,
          );
        this.evaluationLoading = false;
        this.evaluationError = false;
        this.evaluationErrorDetail = '';
      },
      error: () => {
        this.evaluationLoading = false;
        this.evaluationError = true;
        this.evaluationErrorDetail = '';
      },
    });
  }

  private extractTagFromLabel(label: string): string {
    const match = label.match(/\(([^)]+)\)$/);
    return match ? match[1] : label;
  }

  private formatDate(date: Date): string {
    return date.toISOString();
  }

  private filterPiDataByDateRange(
    piData: PiPoints,
    startDate: Date,
    endDate: Date,
  ): PiPoints {
    const startTime = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      0,
      0,
      0,
      0,
    ).getTime();
    const endTime = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      23,
      59,
      59,
      999,
    ).getTime();

    return {
      points: piData.points.map((point) => {
        const filteredData = point.ts.reduce(
          (acc, timestamp, index) => {
            const time = new Date(timestamp).getTime();
            if (time >= startTime && time <= endTime) {
              acc.ts.push(timestamp);
              acc.values.push(point.values[index]);
            }
            return acc;
          },
          { ts: [] as string[], values: [] as number[] },
        );
        return { ...point, ...filteredData };
      }),
    };
  }

  private updateChartWithFilteredData(): void {
    if (!this.cachedPiPointsData || !this.cachedPiPointDetails) return;

    const startDate = this.formGroup.get('evaluationStartDate')?.value;
    const endDate = this.formGroup.get('evaluationEndDate')?.value;
    if (!startDate || !endDate) return;

    // Update large period warning
    const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime();
    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    this.largePeriodWarning = diffMs > oneYearMs;

    // Update chart time axis options based on period length
    const threeMonthsMs = 90 * 24 * 60 * 60 * 1000;
    const periodKey =
      diffMs > threeMonthsMs ? TIME_FRAME.SIXMONTH : TIME_FRAME.THREEMONTH;
    this.piPointsChartOptions = DefaultChartOptions.getOptions(
      periodKey,
      'MMM dd, HH:mm',
      'Valeur',
      false,
    );

    const filteredData = this.filterPiDataByDateRange(
      this.cachedPiPointsData,
      startDate,
      endDate,
    );
    this.interactivePiChartDatasets = PiDataPointsDatasetConverter.getChartData(
      filteredData,
      this.cachedPiPointDetails,
    );
  }

  getFieldError(fieldName: string): string | null {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.invalid || !control.touched) return null;
    const errors = control.errors;
    if (!errors) return null;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['min']) return `Minimum: ${errors['min'].min}`;
    if (errors['max']) return `Maximum: ${errors['max'].max}`;
    if (errors['dateRangeInvalid'])
      return 'La date de début doit être antérieure à la date de fin';
    if (errors['dateRangeTooLarge'])
      return 'La période ne peut pas dépasser un an';
    if (errors['minGreaterThanTotal'])
      return 'Minimum ne peut pas dépasser le total';
    if (errors['totalLessThanMin'])
      return 'Total ne peut pas être inférieur au minimum';
    return 'Valeur invalide';
  }

  private onEvaluationModeChange(mode: string): void {
    const modeMap: { [key: string]: { min: number; total: number } } = {
      '1/1': { min: 1, total: 1 },
      '2/3': { min: 2, total: 3 },
      '4/5': { min: 4, total: 5 },
    };
    if (modeMap[mode]) {
      this.formGroup.patchValue({
        minEvaluationsToTriggerAlarm: modeMap[mode].min,
        evaluationsToConsiderForAlarm: modeMap[mode].total,
      });
    }
  }
}
